import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, GripVertical, Plus, Trash2 } from 'lucide-react'
import { Reorder, useDragControls } from 'framer-motion'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { useHeaderExtra, useHeaderRight } from '@/contexts/header.context'
import { useAppAuth } from '@/contexts/app-auth.context'
import { DirectoryProvider, useDirectory } from '@/contexts/directory.context'
import { useDocumentHead } from '@/hooks/use-document-head'
import { actionsApiClient } from '@/lib/api/actions.client'
import { actionsCatalogApiClient } from '@/lib/api/actions-catalog.client'
import {
  describeAction,
  directoryLookupFrom,
  searchHaystackForAction,
  type DescribableAction,
  type DirectoryLookup,
} from '@/lib/actions/describe'
import { pathFor, ROUTE_REGISTRY } from '@/lib/routes'
import { confirmDialog } from '@/lib/utils/window-utils'
import type {
  NodeActionGroup,
  SaveNodeActionGroupDto,
  WorkflowActionCatalogEntry,
} from '@super/shared-types'

const PICKER_VISIBLE = 10

interface MemberRow {
  key: string
  workflowActionId: string
  snapshot: DescribableAction & {
    name?: string | null
    description?: string | null
    integrationId?: string | null
    sourceRoleMemberId?: string | null
  }
}

function snapshotFromCatalog(
  entry: WorkflowActionCatalogEntry,
): MemberRow['snapshot'] {
  return {
    name: entry.resolvedName ?? entry.name,
    actionType: entry.actionType,
    actionSubtype: entry.actionSubtype,
    parameters: entry.parameters,
    description: entry.description,
    integrationId: entry.integrationId,
    sourceRoleMemberId: entry.sourceRoleMemberId,
    contacts: entry.contacts,
  }
}

export function ActionGroupDetailPage() {
  return (
    <FloatingActionIslandProvider>
      <DirectoryProvider>
        <ActionGroupDetailPageContent />
      </DirectoryProvider>
    </FloatingActionIslandProvider>
  )
}

function ActionGroupDetailPageContent() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const { roles, staff } = useDirectory()
  const directoryLookup = useMemo<DirectoryLookup>(
    () => directoryLookupFrom(roles, staff),
    [roles, staff],
  )
  const isNew = !id || id === 'new'

  useDocumentHead({ title: isNew ? 'New action group' : 'Edit action group' })
  useHeaderExtra(
    <Link
      to={ROUTE_REGISTRY.NODE_ACTIONS.path}
      className="inline-flex items-center gap-1.5 h-8 px-2 rounded-md hover:bg-accent transition-colors text-sm text-muted-foreground"
    >
      <ArrowLeft className="w-4 h-4" />
      Back to actions
    </Link>,
    [],
  )

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [members, setMembers] = useState<MemberRow[]>([])
  const [catalog, setCatalog] = useState<WorkflowActionCatalogEntry[]>([])
  const [catalogLoading, setCatalogLoading] = useState(true)
  const [nameError, setNameError] = useState(false)
  const [membersError, setMembersError] = useState(false)
  const [usageCount, setUsageCount] = useState(0)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const baseline = useRef<{
    name: string
    description: string
    memberIds: string[]
  }>({ name: '', description: '', memberIds: [] })

  const load = useCallback(async () => {
    const scoped = isSuperAdmin && currentCompany
    const catalogList = scoped
      ? await actionsCatalogApiClient.getActionsForCompanyByAdmin(
          currentCompany!.id,
        )
      : await actionsCatalogApiClient.getActions()
    setCatalog(catalogList)
    setCatalogLoading(false)
    if (isNew || !id) return
    const group: NodeActionGroup | null = scoped
      ? await actionsApiClient.getActionForCompanyByAdmin(
          id,
          currentCompany!.id,
        )
      : await actionsApiClient.getActionById(id)
    if (!group) {
      toast.error('Group not found')
      navigate(pathFor(ROUTE_REGISTRY.NODE_ACTIONS.id))
      return
    }
    const loadedName = group.name ?? ''
    const loadedDescription = group.description ?? ''
    const loadedMembers: MemberRow[] = (group.members ?? [])
      .slice()
      .sort((a, b) => a.executionOrder - b.executionOrder)
      .map(m => ({
        key: crypto.randomUUID(),
        workflowActionId: m.workflowActionId,
        snapshot: {
          name: m.workflowAction.name,
          actionType: m.workflowAction.actionType,
          actionSubtype: m.workflowAction.actionSubtype,
          parameters: m.workflowAction.parameters,
          integrationId: m.workflowAction.integrationId,
          sourceRoleMemberId: m.workflowAction.sourceRoleMemberId,
          contacts: m.workflowAction.contacts ?? [],
        },
      }))
    baseline.current = {
      name: loadedName,
      description: loadedDescription,
      memberIds: loadedMembers.map(m => m.workflowActionId),
    }
    setName(loadedName)
    setDescription(loadedDescription)
    setMembers(loadedMembers)
    const usage = scoped
      ? await actionsApiClient.getActionUsageForCompanyByAdmin(
          id,
          currentCompany!.id,
        )
      : await actionsApiClient.getActionUsage(id)
    setUsageCount(usage?.count ?? 0)
    setLoading(false)
  }, [id, isNew, isSuperAdmin, currentCompany, navigate])

  useEffect(() => {
    void load()
  }, [load])

  const catalogById = useMemo(
    () => new Map(catalog.map(c => [c.id, c])),
    [catalog],
  )

  const availableToAdd = useMemo(() => {
    const used = new Set(members.map(m => m.workflowActionId))
    // A group cannot mix TRANSFER with non-TRANSFER actions: the picker on a
    // Transfer node resolves a group to its single TRANSFER member, so a
    // mixed group would be unpickable there and would confuse the author
    // elsewhere. The API also rejects a mixed group on save.
    const hasTransfer = members.some(m => m.snapshot?.actionType === 'TRANSFER')
    const hasNonTransfer = members.some(
      m => m.snapshot?.actionType && m.snapshot.actionType !== 'TRANSFER',
    )
    return catalog
      .filter(c => !used.has(c.id))
      .filter(c => {
        if (hasTransfer && c.actionType !== 'TRANSFER') return false
        if (hasNonTransfer && c.actionType === 'TRANSFER') return false
        return true
      })
  }, [catalog, members])

  function addMember(workflowActionId: string) {
    const entry = catalogById.get(workflowActionId)
    if (!entry) return
    setMembers(prev => [
      ...prev,
      {
        key: crypto.randomUUID(),
        workflowActionId,
        snapshot: snapshotFromCatalog(entry),
      },
    ])
    setMembersError(false)
  }
  function removeMember(key: string) {
    setMembers(prev => prev.filter(m => m.key !== key))
  }

  const isDirty = useMemo(() => {
    const current = {
      name,
      description,
      memberIds: members.map(m => m.workflowActionId),
    }
    const b = baseline.current
    if (current.name !== b.name) return true
    if (current.description !== b.description) return true
    if (current.memberIds.length !== b.memberIds.length) return true
    for (let i = 0; i < current.memberIds.length; i++) {
      if (current.memberIds[i] !== b.memberIds[i]) return true
    }
    return false
  }, [name, description, members])

  async function handleSave() {
    const issues: string[] = []
    const nameValid = name.trim().length > 0
    const membersValid = members.length > 0
    if (!nameValid) issues.push('Name is required')
    if (!membersValid) issues.push('Add at least one action')
    setNameError(!nameValid)
    setMembersError(!membersValid)
    if (issues.length > 0) {
      toast.error(issues.join(' · '))
      if (!nameValid) nameInputRef.current?.focus()
      return
    }
    setSaving(true)
    const dto: SaveNodeActionGroupDto = {
      name: name.trim(),
      description: description.trim() || null,
      actions: members.map(m => ({
        workflowActionId: m.workflowActionId,
        actionType: m.snapshot.actionType,
        actionSubtype: m.snapshot.actionSubtype ?? null,
        parameters: m.snapshot.parameters,
        integrationId: m.snapshot.integrationId ?? null,
        sourceRoleMemberId: m.snapshot.sourceRoleMemberId ?? null,
      })),
    }
    const result = isNew
      ? isSuperAdmin && currentCompany
        ? await actionsApiClient.createActionForCompany(currentCompany.id, dto)
        : await actionsApiClient.createAction(dto)
      : isSuperAdmin && currentCompany
        ? await actionsApiClient.updateActionForCompany(
            id!,
            currentCompany.id,
            dto,
          )
        : await actionsApiClient.updateAction(id!, dto)
    setSaving(false)
    if (!result) {
      toast.error('Save failed')
      return
    }
    toast.success(isNew ? 'Group created' : 'Group saved')
    navigate(pathFor(ROUTE_REGISTRY.NODE_ACTIONS.id))
  }

  useHeaderRight(
    !isNew ? (
      <Button
        type="button"
        variant="destructive"
        size="sm"
        onClick={() => void handleDelete()}
      >
        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
        Delete
      </Button>
    ) : null,
    [isNew, id],
  )

  async function handleDelete() {
    if (!id || isNew) return
    const label = name.trim() || 'this group'
    const message =
      usageCount === 0
        ? `Delete "${label}"? It isn't referenced by any graph.`
        : `Delete "${label}"? ${usageCount} graph${usageCount === 1 ? '' : 's'} will break.`
    const ok = await confirmDialog(message)
    if (!ok) return
    const deleted =
      isSuperAdmin && currentCompany
        ? await actionsApiClient.deleteActionForCompany(id, currentCompany.id)
        : await actionsApiClient.deleteAction(id)
    if (deleted) {
      toast.success('Group deleted')
      navigate(pathFor(ROUTE_REGISTRY.NODE_ACTIONS.id))
    } else {
      toast.error('Could not delete the group')
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-y-auto pt-16">
        <div className="space-y-6 p-6 pt-0 pb-24 max-w-2xl mt-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto pt-16">
      <div className="space-y-6 p-6 pt-0 pb-24 max-w-2xl mt-6">
        <PageHeader
          title={isNew ? 'Create Action Group' : 'Edit Action Group'}
        />

        <Card>
          <CardHeader>
            <CardTitle>Basic Info</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-1 mb-6">
              <Label
                htmlFor="group-name"
                data-error={nameError}
                className="data-[error=true]:text-destructive px-3"
              >
                Name
              </Label>
              <Input
                id="group-name"
                ref={nameInputRef}
                value={name}
                onChange={e => {
                  setName(e.target.value)
                  if (e.target.value.trim()) setNameError(false)
                }}
                placeholder="Email notification group for After hours"
                aria-invalid={nameError}
                aria-describedby={nameError ? 'group-name-error' : undefined}
              />
              {nameError && (
                <p
                  id="group-name-error"
                  className="text-destructive text-xs ml-3"
                >
                  Name is required
                </p>
              )}
            </div>

            <div className="grid gap-1">
              <Label htmlFor="group-description" className="px-3">
                Description
              </Label>
              <Textarea
                id="group-description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe this grouped bundle of actions"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Actions in this group</CardTitle>
            <AddCatalogAction
              available={availableToAdd}
              loading={catalogLoading}
              directory={directoryLookup}
              onPick={addMember}
            />
          </CardHeader>
          <CardContent>
            {members.length === 0 ? (
              <div className="grid gap-1">
                <div
                  aria-invalid={membersError}
                  aria-describedby={
                    membersError ? 'group-members-error' : undefined
                  }
                  className={cn(
                    'rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground',
                    'aria-invalid:border-destructive',
                  )}
                >
                  Add one or more actions from the catalog. Drag to rearrange
                  actions in their order of operation
                </div>
                {membersError && (
                  <p
                    id="group-members-error"
                    className="text-destructive text-xs ml-3"
                  >
                    Add at least one action
                  </p>
                )}
              </div>
            ) : (
              // framer-motion Reorder is pointer-only; keyboard reorder is a follow-up.
              <Reorder.Group
                axis="y"
                values={members}
                onReorder={setMembers}
                className="space-y-2"
              >
                {members.map(member => (
                  <MemberRowItem
                    key={member.key}
                    member={member}
                    directory={directoryLookup}
                    onRemove={() => removeMember(member.key)}
                  />
                ))}
              </Reorder.Group>
            )}
          </CardContent>
        </Card>
      </div>

      <FloatingActionIslandContent
        show={members.length > 0 && isDirty}
        componentId="action-group-detail-form"
        priority={20}
      >
        <div className="inline-flex w-full max-w-2xl">
          <div className="flex flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none mr-4">
            <span className="font-medium">
              Saving "{name.trim() || 'Untitled'}" group
            </span>
          </div>
          <div className="flex items-center space-x-2 justify-end pointer-events-auto ml-auto">
            <Button
              onClick={handleSave}
              disabled={saving || nameError || membersError}
              size="sm"
              variant="form"
            >
              <span>{saving ? 'Saving…' : isNew ? 'Create' : 'Update'}</span>
            </Button>
          </div>
        </div>
      </FloatingActionIslandContent>
      <FloatingActionIsland />
    </div>
  )
}

interface MemberRowItemProps {
  member: MemberRow
  directory: DirectoryLookup
  onRemove: () => void
}

function MemberRowItem({ member, directory, onRemove }: MemberRowItemProps) {
  const controls = useDragControls()
  return (
    <Reorder.Item
      value={member}
      dragListener={false}
      dragControls={controls}
      className="flex items-center gap-3 rounded-md border bg-background p-3"
    >
      <button
        type="button"
        onPointerDown={e => controls.start(e)}
        className="cursor-grab text-muted-foreground touch-none active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {member.snapshot.name ?? describeAction(member.snapshot, directory)}
        </div>
        {member.snapshot.name && (
          <div className="text-xs text-muted-foreground truncate">
            {describeAction(member.snapshot, directory)}
          </div>
        )}
        {member.snapshot.description && (
          <div className="text-xs text-muted-foreground truncate">
            {member.snapshot.description}
          </div>
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="text-muted-foreground hover:text-destructive"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </Reorder.Item>
  )
}

interface AddCatalogActionProps {
  available: WorkflowActionCatalogEntry[]
  loading: boolean
  directory: DirectoryLookup
  onPick: (workflowActionId: string) => void
}

function AddCatalogAction({
  available,
  loading,
  directory,
  onPick,
}: AddCatalogActionProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled>
        Loading catalog…
      </Button>
    )
  }
  if (available.length === 0) {
    return (
      <Button variant="outline" size="sm" disabled>
        <Plus className="h-4 w-4 mr-1" />
        Catalog exhausted
      </Button>
    )
  }
  const needle = query.trim().toLowerCase()
  const matches = needle
    ? available.filter(entry =>
        searchHaystackForAction(entry, directory).includes(needle),
      )
    : available.slice(0, PICKER_VISIBLE)
  const hidden = needle
    ? available.length - matches.length
    : available.length - matches.length

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-1" />
          Add action
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder="Search actions by name, recipient, or description"
          />
          <CommandList>
            <CommandEmpty>No actions match this search.</CommandEmpty>
            <CommandGroup>
              {matches.map(entry => (
                <CommandItem
                  key={entry.id}
                  value={entry.id}
                  onSelect={() => {
                    onPick(entry.id)
                    setQuery('')
                    setOpen(false)
                  }}
                  className="flex flex-col items-start gap-0.5"
                >
                  <span className="font-medium">
                    {entry.resolvedName ??
                      entry.name ??
                      describeAction(entry, directory)}
                  </span>
                  {(entry.resolvedName ?? entry.name) && (
                    <span className="text-xs text-muted-foreground truncate w-full">
                      {describeAction(entry, directory)}
                    </span>
                  )}
                  {entry.description && (
                    <span className="text-xs text-muted-foreground truncate w-full">
                      {entry.description}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            {!needle && hidden > 0 && (
              <div className="border-t px-3 py-2 text-xs text-muted-foreground">
                + {hidden} more. Type to narrow.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
